import { useState, useEffect } from 'react';
import { sanityClient } from '../lib/sanityClient';
import { PACKAGE_BY_SLUG } from '../lib/queries';
import { urlFor } from '../lib/urlFor';
import type { TourDetail, DayItinerary } from '../data/tourData';

interface SanityItineraryDay {
  _key?: string;
  day_number: number;
  title: string;
  description?: string;
}

export interface SanityImageRef {
  _type: 'image';
  asset: { _ref: string; _type: 'reference' };
  alt?: string;
}

export interface SanityTourDetail {
  _id: string;
  title: string;
  slug: { current: string };
  destination: string;
  region?: string;
  theme?: string[];
  duration_days: number;
  duration_nights: number;
  price_per_person_aed: number;
  is_featured: boolean;
  hotel_name?: string;
  hotel_stars?: number;
  hero_image: SanityImageRef;
  gallery?: SanityImageRef[];
  description?: string;
  inclusions?: string[];
  exclusions?: string[];
  highlights?: string[];
  itinerary?: SanityItineraryDay[];
  cancellationPolicy?: string;
  cancellation_policy?: string;
  faqs?: { q?: string; question?: string; a?: string; answer?: string }[];
  body?: unknown;
}

export interface RelatedPackage {
  _id: string;
  title: string;
  slug: { current: string };
  destination: string;
  region?: string;
  theme?: string[];
  duration_days: number;
  duration_nights: number;
  price_per_person_aed: number;
  hero_image: SanityImageRef;
  hotel_stars?: number;
  is_featured: boolean;
}

interface UseTourPackageResult {
  loading: boolean;
  error: string | null;
  tour: TourDetail | null;
  related: RelatedPackage[];
}

export function mapSanityToTourDetail(pkg: SanityTourDetail): TourDetail {
  const faqs = (pkg.faqs ?? [])
    .map(faq => ({
      q: faq.q ?? faq.question ?? '',
      a: faq.a ?? faq.answer ?? '',
    }))
    .filter(faq => faq.q && faq.a);

  return {
    id: 0,
    slug: pkg.slug.current,
    name: pkg.title,
    destination: pkg.destination,
    tagline: `Experience the best of ${pkg.destination}`,
    description: pkg.description ? [pkg.description] : [],
    region: pkg.region ?? 'International',
    types: pkg.theme ?? [],
    duration: pkg.duration_days,
    nights: pkg.duration_nights,
    price: pkg.price_per_person_aed ?? 0,
    rating: 4.8,
    reviewCount: 0,
    stars: pkg.hotel_stars ?? 4,
    hotelName: pkg.hotel_name ?? 'Deluxe Hotel',
    hotelLocation: pkg.destination,
    departure: `Dubai -> ${pkg.destination}`,
    groupSize: 'Up to 20 pax',
    popular: pkg.is_featured,
    badge: pkg.is_featured ? 'Featured' : undefined,
    heroImage: pkg.hero_image ? urlFor(pkg.hero_image).width(1400).url() : '',
    gallery: (pkg.gallery ?? [])
      .filter(img => Boolean(img.asset?._ref))
      .slice(0, 4)
      .map(img => urlFor(img).width(700).url()),
    highlights: pkg.highlights ?? [],
    itinerary: (pkg.itinerary ?? []).map((d): DayItinerary => ({
      day: d.day_number,
      title: d.title,
      description: d.description ?? '',
      activities: [],
      meals: 'Inquire for full details',
      accommodation: pkg.hotel_name ?? 'Hotel included',
    })),
    inclusions: pkg.inclusions ?? [],
    exclusions: pkg.exclusions ?? [],
    importantNotes: [],
    cancellationPolicy: pkg.cancellationPolicy ?? pkg.cancellation_policy,
    faqs,
    bestTime: 'Year-round - contact us for seasonal recommendations',
    reviews: [],
  };
}

export function useTourPackage(
  slug: string,
  initialTour: TourDetail | null = null,
  initialRelated: RelatedPackage[] = []
): UseTourPackageResult {
  const [loading, setLoading] = useState(!initialTour);
  const [error, setError] = useState<string | null>(null);
  const [tour, setTour] = useState<TourDetail | null>(initialTour);
  const [related, setRelated] = useState<RelatedPackage[]>(initialRelated);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    sanityClient
      .fetch<SanityTourDetail>(PACKAGE_BY_SLUG, { slug })
      .then(async data => {
        if (!data) {
          setTour(null);
          return;
        }
        setTour(mapSanityToTourDetail(data));

        const relatedData = await sanityClient.fetch<RelatedPackage[]>(
            `*[_type == "tourPackage" && slug.current != $slug && is_published == true && (
              destination == $destination || region == $region || count((theme[])[@ in $theme]) > 0
            )] | order(is_featured desc, price_per_person_aed asc)[0...3] {
              _id, title, slug, destination, region, theme,
              duration_days, duration_nights, price_per_person_aed,
              hero_image, hotel_stars, is_featured
            }`,
            { destination: data.destination, region: data.region ?? '', theme: data.theme ?? [], slug }
        );
        setRelated(relatedData ?? []);
      })
      .catch(err => setError((err as Error).message ?? 'Failed to load tour'))
      .finally(() => setLoading(false));
  }, [slug]);

  return { loading, error, tour, related };
}
